import {
  completeAction,
  logAiCall,
  logApiCall,
  logError,
  logPerformance,
  startAction,
  updateAction,
} from "./logger";

const getCorrelationId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return `corr-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const getActionIdFromResponse = (response) =>
  response?.action_id || response?.actionId || response?.id || response?.data?.id;

export const createProductFlowLogger = ({
  companyId,
  userId,
  entityId = null,
}) => {
  const correlationId = getCorrelationId();
  let actionId = null;
  let actionStarted = false;

  const basePayload = {
    company_id: companyId,
    user_id: userId,
    action_type: "PRODUCT_LISTING",
    module: "PRODUCT",
    entity_type: "PRODUCT",
    entity_id: entityId,
    source: "WEB",
    correlation_id: correlationId,
  };

  const ensureStarted = async (initialPayload = {}) => {
    if (actionStarted) return actionId;

    try {
      const response = await startAction({
        ...basePayload,
        ...initialPayload,
      });

      actionId = getActionIdFromResponse(response);
      actionStarted = Boolean(actionId);
    } catch (error) {
      console.error("Failed to start product flow action:", error);
    }

    return actionId;
  };

  const logStep = async ({ progress, currentStep, entity_id, extra = {} }) => {
    await ensureStarted();
    if (!actionId) return;

    try {
      await updateAction(actionId, {
        progress,
        current_step: currentStep,
        entity_id: entity_id ?? basePayload.entity_id,
        ...extra,
      });
    } catch (error) {
      console.error("Failed to update product flow action:", error);
    }
  };

  const complete = async ({ status, progress, currentStep, entity_id, error }) => {
    await ensureStarted();
    if (!actionId) return;

    try {
      await completeAction(actionId, {
        status,
        progress,
        current_step: currentStep,
        entity_id: entity_id ?? basePayload.entity_id,
        ...(error ? { error_message: error } : {}),
      });
    } catch (completeError) {
      console.error("Failed to complete product flow action:", completeError);
    }
  };

  const safeLog = async (fn) => {
    try {
      await fn();
    } catch (error) {
      // Observability failures must never break product flow
      console.error("Observability log failed:", error);
    }
  };

  const startTimer = (step) => ({
    step,
    started_at: Date.now(),
    start_time_ms:
      typeof performance !== "undefined" && performance.now
        ? performance.now()
        : Date.now(),
  });

  const endTimer = async (timer, metadata = {}) => {
    if (!timer) return;

    const currentMs =
      typeof performance !== "undefined" && performance.now
        ? performance.now()
        : Date.now();
    const durationMs = Math.max(0, Math.round(currentMs - timer.start_time_ms));

    await safeLog(async () => {
      await ensureStarted();
      await logPerformance({
        action_id: actionId,
        service_name: metadata.service_name || "ADD_PRODUCT_PAGE",
        operation: metadata.operation || timer.step,
        duration_ms: durationMs,
        status: metadata.status || "SUCCESS",
        step: metadata.step || timer.step,
        correlation_id: correlationId,
      });
    });
  };

  const apiLog = async (payload) =>
    safeLog(async () => {
      await ensureStarted();
      await logApiCall({
        action_id: actionId,
        company_id: basePayload.company_id,
        correlation_id: correlationId,
        ...payload,
      });
    });

  const aiLog = async (payload) =>
    safeLog(async () => {
      await ensureStarted();
      await logAiCall({
        action_id: actionId,
        company_id: basePayload.company_id,
        correlation_id: correlationId,
        ...payload,
      });
    });

  const errorLog = async (payload) =>
    safeLog(async () => {
      await ensureStarted();
      await logError({
        action_id: actionId,
        correlation_id: correlationId,
        ...payload,
      });
    });

  return {
    correlationId,
    startUpload: () =>
      ensureStarted({
        progress: 10,
        current_step: "UPLOAD",
      }),
    logAutofill: () =>
      logStep({
        progress: 30,
        currentStep: "AUTOFILL",
      }),
    logEbayAutofill: () =>
      logStep({
        progress: 40,
        currentStep: "EBAY_AUTOFILL",
      }),
    logUpscale: () =>
      logStep({
        progress: 60,
        currentStep: "UPSCALE",
      }),
    logBackgroundRemoval: () =>
      logStep({
        progress: 70,
        currentStep: "BACKGROUND_REMOVAL",
      }),
    logCrossListing: (selectedMarketplaces = []) =>
      logStep({
        progress: 90,
        currentStep: "CROSS_LISTING",
        extra: { marketplaces: selectedMarketplaces },
      }),
    getActionId: () => actionId,
    getCorrelationId: () => correlationId,
    startTimer,
    endTimer,
    logApiCall: apiLog,
    logAiCall: aiLog,
    logPerformance: (payload) =>
      safeLog(async () => {
        await ensureStarted();
        await logPerformance({
          action_id: actionId,
          correlation_id: correlationId,
          ...payload,
        });
      }),
    logError: errorLog,
    completeSuccess: ({ entityId: completedEntityId } = {}) =>
      complete({
        status: "SUCCESS",
        progress: 100,
        currentStep: "COMPLETED",
        entity_id: completedEntityId,
      }),
    completeFailed: ({ error, entityId: completedEntityId } = {}) =>
      complete({
        status: "FAILED",
        progress: 100,
        currentStep: "FAILED",
        entity_id: completedEntityId,
        error,
      }),
  };
};
